'use client';

import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import { fetchProfile } from '@/lib/api/profile-client';
import {
  hasMeaningfulPreview,
  primeUserPreviewCacheFromPreview,
  resolveUserPreviewCacheLookup,
  upsertUserPreviewCacheEntry,
} from '@/lib/cache/user-preview-cache';
import { isRecord } from '@/lib/utils/type-guards';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import { fromProfileResponse } from '@/lib/view-models/user-preview';
import { userPreviewStore } from '@/store/user-preview/user-preview-store';

export type UserData = UserPreviewVM;

type UserPreviewLike = {
  id?: unknown;
  username?: unknown;
  name?: unknown;
  verifiedType?: unknown;
};

const isUserPreviewData = (input: unknown): input is UserData => {
  if (!isRecord<UserPreviewLike>(input)) {
    return false;
  }
  return (
    typeof input.id === 'string' &&
    typeof input.username === 'string' &&
    typeof input.name === 'string' &&
    typeof input.verifiedType === 'string'
  );
};

interface PrefetchOptions {
  cacheKey: string | null;
  lookupProfileId: string | null;
  lookupUsername: string | null;
  prefetched?: Partial<UserData>;
  prefetchedHasData: boolean;
  primed: boolean;
  profileIdProp: string | null | undefined;
  setError: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setUser: Dispatch<SetStateAction<UserData | null>>;
}

export function usePrefetchUser({
  primed,
  lookupProfileId,
  lookupUsername,
  prefetched,
  prefetchedHasData,
  cacheKey,
  profileIdProp,
  setUser,
  setError,
  setLoading,
}: PrefetchOptions): void {
  const inFlightReference = useRef<{ cacheKey: string | null; inFlight: boolean }>({ cacheKey: null, inFlight: false });

  const lookup = useMemo(
    () =>
      resolveUserPreviewCacheLookup({
        cacheKey,
        profileId: lookupProfileId,
        username: lookupUsername,
      }),
    [cacheKey, lookupProfileId, lookupUsername]
  );

  const cached = useStore(userPreviewStore, (state) => {
    const resolvedKey = state.resolvePrimaryKey(lookup);
    return resolvedKey ? state.getEntry(resolvedKey) : null;
  });

  /* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent -- The hover card subscribes to the shared preview cache; when entries change we must push the latest snapshot into the caller's local state. */
  useEffect(() => {
    if (!cached) {
      return;
    }

    const preview = cached.preview;
    if (preview) {
      setUser((current) => {
        if (!current || current.id === preview.id) {
          return preview;
        }
        return current;
      });
    } else if (cached.error) {
      setUser(() => null);
    }

    setError(cached.error);
  }, [cached, setError, setUser]);
  /* eslint-enable react-you-might-not-need-an-effect/no-pass-data-to-parent */

  /* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent -- Prefetch orchestration has to notify the parent hooks as network state changes so the hover UI can show loading and fallback states. */
  useEffect(() => {
    if (!primed) {
      return;
    }
    const hasLookupKey = Boolean(lookupProfileId || lookupUsername);
    if (!hasLookupKey) {
      return;
    }

    const prefetchedUser = prefetchedHasData && prefetched && isUserPreviewData(prefetched) ? prefetched : null;
    if (prefetchedUser) {
      primeUserPreviewCacheFromPreview(prefetchedUser);
      setUser((current) => {
        if (!current || current.id !== prefetchedUser.id) {
          return prefetchedUser;
        }
        return current;
      });
    }

    const hasCompleteCachedData = cached?.detail === 'full' && (cached.profile !== undefined || cached.error);
    if (hasCompleteCachedData) {
      return;
    }

    let cancelled = false;

    const showErrorState = () => {
      setUser(() => null);
      setError(true);
    };

    const shouldCacheUser = (data: UserData) => hasMeaningfulPreview(data);

    const commitUserData = (data: UserData) => {
      setUser(data);
      if (shouldCacheUser(data)) {
        upsertUserPreviewCacheEntry(cacheKey, {
          preview: data,
          error: false,
          detail: 'partial',
          profileId: lookupProfileId,
          username: lookupUsername,
        });
      }
    };

    const logPreviewError = (error: unknown) => {
      let label = 'unknown user';
      if (lookupUsername) {
        label = `@${lookupUsername}`;
      } else if (profileIdProp) {
        label = `id:${profileIdProp}`;
      }
      console.error(`Failed to fetch user preview for ${label}:`, error);
    };

    const handleNotFound = () => {
      showErrorState();
      upsertUserPreviewCacheEntry(cacheKey, {
        preview: null,
        profile: null,
        error: true,
        detail: 'full',
        profileId: lookupProfileId,
        username: lookupUsername,
      });
    };

    const fetchUser = async () => {
      inFlightReference.current = { cacheKey, inFlight: true };
      setLoading(true);
      setError(false);

      try {
        const profile = await fetchProfile({
          profileId: lookupProfileId,
          username: lookupUsername,
        });
        if (!profile) {
          handleNotFound();
          return;
        }
        const data: UserData = fromProfileResponse(profile);
        if (!cancelled) {
          commitUserData(data);
        }
        upsertUserPreviewCacheEntry(cacheKey, {
          preview: data,
          profile,
          error: false,
          detail: 'full',
          profileId: lookupProfileId,
          username: lookupUsername,
        });
      } catch (error) {
        logPreviewError(error);
        if (!cancelled) {
          handleNotFound();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        if (inFlightReference.current.cacheKey === cacheKey) {
          inFlightReference.current = { cacheKey, inFlight: false };
        }
      }
    };

    if (inFlightReference.current.inFlight && inFlightReference.current.cacheKey === cacheKey) {
      return;
    }

    fetchUser().catch(() => {
      // Errors are handled inside fetchUser; this prevents unhandled rejections from tripping lint rules.
    });

    return () => {
      cancelled = true;
      if (inFlightReference.current.cacheKey === cacheKey) {
        inFlightReference.current = { cacheKey, inFlight: false };
      }
    };
  }, [
    cached,
    cacheKey,
    lookupProfileId,
    lookupUsername,
    prefetched,
    prefetchedHasData,
    primed,
    profileIdProp,
    setError,
    setLoading,
    setUser,
  ]);
  /* eslint-enable react-you-might-not-need-an-effect/no-pass-data-to-parent */
}
